import { Component, EventEmitter, Output, OnInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { InputComponent } from '../../input/input.component';
import { ButtonComponent } from '../../button/button.component';
import { FilterStateService } from '../../../services/filter-state.service';
import { SuggestionsService, SuggestionCompany } from '../../../services/suggestions.service';
import { LanguageRoutingService } from '../../../services/language-routing.service';
import { PlacesAutocompleteComponent } from '../../../../../../../libs/shared/src/lib/google/places-autocomplete/places-autocomplete.component';
import { Address } from '../../../../../../../libs/shared/src/lib/models/address.model';

import { Subject, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, map } from 'rxjs/operators';

@Component({
  selector: 'app-job-hero',
  standalone: true,
  imports: [CommonModule, FormsModule, InputComponent, ButtonComponent, TranslocoModule, PlacesAutocompleteComponent],
  templateUrl: './job-hero.component.html',
  styleUrls: ['./job-hero.component.scss'],
})
export class JobHeroComponent implements OnInit, OnDestroy {
  @Output() openAdvancedSearch = new EventEmitter<void>();
  @Output() search = new EventEmitter<{ keyword: string; location: string }>();
  @Output() clearLocation = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private initialized = false;

  keyword = '';
  location = '';
  selectedAddress: Address | null = null;
  searchRadius = 10;

  // --- Search typeahead state ---
  private readonly minQueryLength = 2;
  private keywordInput$ = new Subject<string>();
  suggestionQuery = '';
  suggestionTitles: string[] = [];
  suggestionCompanies: SuggestionCompany[] = [];
  suggestionsOpen = false;
  suggestionsLoading = false;

  constructor(
    private filterStateService: FilterStateService,
    private suggestionsService: SuggestionsService,
    private router: Router,
    private languageRoutingService: LanguageRoutingService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  get hasAnySuggestions(): boolean {
    return this.suggestionTitles.length > 0 || this.suggestionCompanies.length > 0;
  }

  get showEmptyState(): boolean {
    return (
      this.suggestionsOpen &&
      !this.suggestionsLoading &&
      !this.hasAnySuggestions &&
      this.suggestionQuery.length >= this.minQueryLength
    );
  }

  get showSuggestionsPanel(): boolean {
    return this.suggestionsOpen && (this.hasAnySuggestions || this.showEmptyState);
  }

  ngOnInit(): void {
    // Get initial values from filter state
    const initialState = this.filterStateService.getCurrentFilterState();
    this.keyword = initialState.searchKeyword || '';
    this.location = initialState.searchLocation || '';
    this.searchRadius = initialState.searchRadius || 10;

    // Restore selectedAddress if we have lat/lng from URL
    if (initialState.searchLatitude && initialState.searchLongitude) {
      this.selectedAddress = {
        formattedAddress: initialState.searchLocation,
        latitude: initialState.searchLatitude,
        longitude: initialState.searchLongitude
      };
    }

    this.initialized = true;

    // Subscribe to search keyword changes to sync with filter state (only for external changes like clear)
    this.filterStateService.searchKeyword$
      .pipe(takeUntil(this.destroy$))
      .subscribe(keyword => {
        // Only update if different to avoid conflicts with user typing, and skip first emission if already initialized
        if (this.initialized && this.keyword !== keyword) {
          this.keyword = keyword || '';
        }
      });

    // Subscribe to search location changes to sync with filter state (only for external changes like clear)
    this.filterStateService.searchLocation$
      .pipe(takeUntil(this.destroy$))
      .subscribe(location => {
        // Only update if different to avoid conflicts with user typing, and skip first emission if already initialized
        if (this.initialized && this.location !== location) {
          this.location = location || '';
          // If location is cleared, also clear selectedAddress
          if (!location) {
            this.selectedAddress = null;
          }
        }
      });

    // Subscribe to search radius changes
    this.filterStateService.searchRadius$
      .pipe(takeUntil(this.destroy$))
      .subscribe(radius => {
        if (this.initialized && radius !== null && this.searchRadius !== radius) {
          this.searchRadius = radius;
        } else if (radius === null) {
          this.searchRadius = 10; // Reset to default
        }
      });

    // Typeahead: debounce keystrokes, then fetch title and company suggestions.
    this.keywordInput$
      .pipe(
        debounceTime(200),
        map((value) => value.trim()),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query.length < this.minQueryLength) {
            return of({ query, titles: [], companies: [] });
          }
          this.suggestionsLoading = true;
          return this.suggestionsService.getSuggestions(query, 5).pipe(
            map((res) => ({ query, titles: res.titles, companies: res.companies }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.suggestionsLoading = false;
        this.suggestionQuery = result.query;
        this.suggestionTitles = result.titles;
        this.suggestionCompanies = result.companies;
        this.suggestionsOpen = result.query.length >= this.minQueryLength;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(): void {
    this.closeSuggestions();
    this.search.emit({ keyword: this.keyword, location: this.location });
  }

  onOpenModal(): void {
    this.openAdvancedSearch.emit();
  }

  onClearKeyword(): void {
    this.keyword = '';
    this.closeSuggestions();
    this.filterStateService.setSearchKeyword('');
    this.onSearch();
  }

  onKeywordInput(value: string): void {
    this.keyword = value;
    this.keywordInput$.next(value ?? '');
  }

  /** Title picked → fill the keyword and run the search. */
  selectSuggestionText(value: string): void {
    this.keyword = value;
    this.closeSuggestions();
    this.onSearch();
  }

  /** Company picked → filter the job list by that company (same as company-card). */
  selectCompany(company: SuggestionCompany): void {
    this.closeSuggestions();
    const lang = this.languageRoutingService.getCurrentLanguage();
    this.router.navigate([lang], { queryParams: { companyIDs: company.id } });
  }

  closeSuggestions(): void {
    this.suggestionsOpen = false;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.suggestionsOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeSuggestions();
    }
  }

  onClearLocation(): void {
    this.clearLocation.emit();
  }

  onLocationSelected(address: Address | null): void {
    if (!address) {
      return;
    }
    this.selectedAddress = address;
    this.location = address.formattedAddress;
    // Use current searchRadius (default 10km)
    this.filterStateService.setSearchLocation(
      address.formattedAddress,
      address.latitude,
      address.longitude,
      this.searchRadius
    );
    this.onSearch();
  }

  onRadiusChange(radius: number): void {
    this.searchRadius = radius;
    if (this.selectedAddress) {
      this.filterStateService.setSearchLocation(
        this.location,
        this.selectedAddress.latitude,
        this.selectedAddress.longitude,
        this.searchRadius
      );
      this.onSearch();
    }
  }
}
